import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';
const getCurrentUser = () => localStorage.getItem('notes_user') || '';

async function fetchWithAuth(url, options = {}) {
  const token = getToken();
  const res = await fetch(`${API_URL}${url}`, { ...options, headers: { ...(options.headers || {}), 'x-token': token } });
  if (res.status === 401) { localStorage.removeItem('notes_token'); localStorage.removeItem('notes_user'); alert('登录超时，请重新登录'); window.location.href = '/'; return null; }
  return res;
}

export default function TaskBoard() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [boardData, setBoardData] = useState({});
  const [allTaskKeys, setAllTaskKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(''); // 版本筛选
  const [availableVersions, setAvailableVersions] = useState([]);
  const [allAttachments, setAllAttachments] = useState([]);
  const [selectedAttachments, setSelectedAttachments] = useState(new Set());
  const [periodDeadlines, setPeriodDeadlines] = useState({}); // 各期数截止日期
  const [initialized, setInitialized] = useState(false); // 用于追踪是否已经设置过默认值

  // Feature 2: 附件去重
  const uniqueAttachments = useMemo(() => {
    return [...new Map(allAttachments.map(a => [a.originalName || a.original_name || a.filename, a])).values()];
  }, [allAttachments]);

  // 项目切换时重置筛选状态 + 加载任务列表
  useEffect(() => {
    setSelectedTask('');
    setSelectedPeriod('');
    setSelectedVersion('');
    setInitialized(false); // 重置初始化标记
    // 初始加载时设置allTaskKeys
    const loadTaskKeys = async () => {
      const res = await fetchWithAuth(`/api/projects/${id}/task-board`);
      if (!res) return;
      const data = await res.json();
      setAllTaskKeys(Object.keys(data));
    };
    loadTaskKeys();
  }, [id]);

  useEffect(() => { fetchProject(); fetchBoard(); }, [id]);

  // 用于追踪是否已经设置过默认值

  const fetchProject = async () => {
    const res = await fetchWithAuth(`/api/projects/${id}`);
    if (!res) return;
    const data = await res.json();
    setProject(data);
  };

  const fetchBoard = async () => {
    setLoading(true);
    let url = `/api/projects/${id}/task-board`;
    const params = [];
    if (selectedTask) params.push(`personalParent=${encodeURIComponent(selectedTask)}`);
    if (selectedPeriod) params.push(`period=${selectedPeriod}`);
    if (selectedVersion) params.push(`version=${selectedVersion}`);
    if (params.length > 0) url += '?' + params.join('&');
    const res = await fetchWithAuth(url);
    if (!res) return;

    const data = await res.json();
    setBoardData(data);
    
    // 收集所有期数（从minPeriod开始）和计算截止日期
    const periods = new Set();
    const deadlines = {};
    let maxCurrentPeriod = 0;
    let maxCompletedPeriod = 0; // 所有人中最大的已完成期数
    Object.values(data).forEach(task => {
      if (task.isRecurring) {
        const minP = task.minPeriod || 1;
        for (let i = minP; i <= task.maxPeriod; i++) {
          periods.add(i);
        }
        // 计算各期截止日期：基于当前期数的plannedEnd反推
        if (task.members && task.members.length > 0 && task.recurringRule) {
          const member = task.members[0];
          const currentPeriod = member.currentPeriod || task.maxPeriod;
          maxCurrentPeriod = Math.max(maxCurrentPeriod, currentPeriod);
          const plannedEnd = member.plannedEnd;
          if (plannedEnd) {
            const weeklyMatch = task.recurringRule.match(/^weekly-(\d)$/);
            const periodDays = weeklyMatch ? 7 : 30;
            for (let i = minP; i <= task.maxPeriod; i++) {
              const diff = (currentPeriod - i) * periodDays;
              const date = new Date(plannedEnd);
              date.setDate(date.getDate() - diff);
              deadlines[i] = date.toISOString().split('T')[0];
            }
          }
        }
        // 找到所有人中最大的已完成期数
        task.members.forEach(m => {
          m.periodRecords?.forEach(r => {
            periods.add(r.period);
            if (r.status === '已完成') {
              maxCompletedPeriod = Math.max(maxCompletedPeriod, r.period);
            }
          });
        });
      }
    });
    setAvailablePeriods([...periods].sort((a, b) => b - a));
    setPeriodDeadlines(deadlines);
    // 收集所有版本ID（后端已返回taskVersions列表）
    const versions = new Set();
    Object.values(data).forEach(taskInfo => {
      (taskInfo.taskVersions || []).forEach(v => versions.add(v));
    });
    setAvailableVersions([...versions].sort());
    
    // 默认选中：最近一个已有人完成的期数
    if (!initialized && periods.size > 0) {
      const defaultPeriod = maxCompletedPeriod > 0 ? maxCompletedPeriod : maxCurrentPeriod;
      setSelectedPeriod(String(defaultPeriod));
      setInitialized(true);
    }
    setLoading(false);
  };

  const fetchAttachments = async () => {
    let url = `/api/projects/${id}/task-attachments`;
    const params = [];
    if (selectedTask) params.push(`personalParent=${encodeURIComponent(selectedTask)}`);
    if (selectedPeriod) params.push(`period=${selectedPeriod}`);
    if (selectedVersion) params.push(`version=${selectedVersion}`);
    if (params.length > 0) url += '?' + params.join('&');
    
    const res = await fetchWithAuth(url);
    if (!res) return;
    const data = await res.json();
    setAllAttachments(data);
    setSelectedAttachments(new Set()); // 清空选中
  };

  useEffect(() => { if (id) fetchBoard(); }, [selectedTask, selectedPeriod, selectedVersion, id]);
  useEffect(() => { fetchAttachments(); }, [selectedTask, selectedPeriod, selectedVersion, id]);

  const formatSize = (bytes) => { if (bytes < 1024) return bytes + 'B'; if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB'; return (bytes / (1024 * 1024)).toFixed(1) + 'MB'; };
  
  const getPeriodStatus = (member, period) => {
    // 如果查询的期数早于该成员任务的起始期数，显示"未开始"
    if (member.startPeriod && period < member.startPeriod) {
      return '未开始';
    }
    if (!member.periodRecords) return '未完成';
    const record = member.periodRecords.find(r => r.period === period);
    return record ? '已完成' : '未完成';
  };
  
  const getStatusColor = (status) => {
    if (status === '已完成') return '#52c41a';
    if (status === '未开始') return '#d9d9d9'; // 灰色
    return '#ff4d4f';
  };
  
  const toggleSelectAll = () => {
    if (selectedAttachments.size === allAttachments.length) {
      setSelectedAttachments(new Set());
    } else {
      setSelectedAttachments(new Set(allAttachments.map(a => a.id)));
    }
  };
  
  const toggleAttachment = (attId) => {
    const newSelected = new Set(selectedAttachments);
    if (newSelected.has(attId)) {
      newSelected.delete(attId);
    } else {
      newSelected.add(attId);
    }
    setSelectedAttachments(newSelected);
  };
  
  const downloadSelected = async () => {
    const selected = allAttachments.filter(a => selectedAttachments.has(a.id));
    for (let i = 0; i < selected.length; i++) {
      const att = selected[i];
      try {
        // 通过后端代理下载，解决跨域问题
        const downloadUrl = `${API_URL}/api/download?cosKey=${encodeURIComponent(att.cosKey)}&filename=${encodeURIComponent(att.originalName || att.filename)}`;
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.click();
        // 延迟避免浏览器阻止
        if (i < selected.length - 1) await new Promise(r => setTimeout(r, 500));
      } catch (e) {
        console.error('下载失败:', att.originalName, e);
      }
    }
  };

  if (loading) return <div className="loading-state">加载中...</div>;
  if (!project) return <div className="empty-state">项目不存在</div>;

  const isOwnerOrAssignee = project.owner === getCurrentUser();

  return (
    <div className="page-container">
      {/* 顶部 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn-secondary" onClick={() => navigate(`/projects/${id}`)} style={{ padding: '4px 12px' }}>← 返回项目</button>
          <h2 style={{ margin: 0, fontSize: 20 }}>{project.name} - 项目看板</h2>
        </div>
      </div>

      {/* 筛选 */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <label style={{ marginRight: 8, fontSize: 14, color: '#666' }}>选择任务：</label>
          <select value={selectedTask} onChange={e => setSelectedTask(e.target.value)} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d9d9d9' }}>
            <option value="">全部任务</option>
            {allTaskKeys.map(key => <option key={key} value={key}>{key}</option>)}
          </select>
        </div>
        {availablePeriods.length > 0 && (
          <div>
            <label style={{ marginRight: 8, fontSize: 14, color: '#666' }}>选择期数：</label>
            <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d9d9d9' }}>
              <option value="">全部期数</option>
              {availablePeriods.map(p => <option key={p} value={p}>第 {p} 期</option>)}
            </select>
          </div>
        )}
        {selectedPeriod && periodDeadlines[selectedPeriod] && (
          <span style={{ padding: '4px 12px', background: '#fff7e6', color: '#fa8c16', borderRadius: 6, fontSize: 13 }}>
            📅 截止日期：{periodDeadlines[selectedPeriod]}
          </span>
        )}
        <div>
            <label style={{ marginRight: 8, fontSize: 14, color: '#666' }}>选择版本：</label>
            <select value={selectedVersion} onChange={e => setSelectedVersion(e.target.value)} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d9d9d9' }}>
              <option value="">全部</option>
              {availableVersions.map(v => <option key={v} value={v}>{v}</option>)}
              {availableVersions.length === 0 && <option value="" disabled>暂无版本</option>}
            </select>
          </div>
      </div>

      {/* 看板列表 */}
      {Object.keys(boardData).length === 0 ? (
        <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>暂无任务数据</div>
      ) : (
        Object.entries(boardData).map(([taskName, taskInfo]) => (
          <div key={taskName} style={{ marginBottom: 24, background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16 }}>{taskName}</h3>
                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                  {taskInfo.isRecurring && <span style={{ padding: '2px 10px', borderRadius: 4, fontSize: 12, background: '#f0f5ff', color: '#2f54eb' }}>周期任务</span>}
                  {taskInfo.isRecurring && <span style={{ padding: '2px 10px', borderRadius: 4, fontSize: 12, background: '#e6f7ff', color: '#1890ff' }}>当前第 {taskInfo.maxPeriod} 期</span>}
                </div>
              </div>
              {/* 模板下载 + 上传 */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {taskInfo.templates && taskInfo.templates.length > 0 && taskInfo.templates.map(tpl => (
                  <span key={tpl.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {tpl.period && <span style={{ padding: '1px 6px', borderRadius: 3, background: '#e6f7ff', color: '#1890ff', fontSize: 10 }}>第{tpl.period}期</span>}
                    <a href={tpl.signedUrl || tpl.cosUrl || `${API_URL}${tpl.url}`} target="_blank" rel="noopener noreferrer"
                      style={{ padding: '4px 12px', background: '#f9f0ff', color: '#722ed1', borderRadius: 6, fontSize: 13, textDecoration: 'none' }}>
                      📥 {tpl.originalName}
                    </a>
                  </span>
                ))}
                {taskInfo.isRecurring && (
                  <label style={{ padding: '4px 12px', background: '#f6ffed', color: '#52c41a', borderRadius: 6, fontSize: 13, cursor: 'pointer', border: '1px solid #b7eb8f' }}>
                    📤 上传模板
                    <input type="file" style={{ display: 'none' }} onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      e.target.value = '';
                      const formData = new FormData();
                      formData.append('file', file);
                      formData.append('personalParent', taskName);
                      formData.append('period', selectedPeriod || String(taskInfo.maxPeriod));
                      try {
                        const res = await fetchWithAuth(`/api/projects/${id}/templates/batch`, { method: 'POST', body: formData });
                        const data = await res.json();
                        if (data.success) {
                          fetchBoard(); // 刷新看板
                        } else {
                          alert(data.error || '上传失败');
                        }
                      } catch (err) { alert('上传失败'); }
                    }} />
                  </label>
                )}
              </div>
            </div>

            {/* 成员完成情况表格 */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <th style={{ padding: '10px 12px', borderBottom: '1px solid #e8e8e8', textAlign: 'left' }}>成员</th>
                  {taskInfo.isRecurring && selectedPeriod && <th style={{ padding: '10px 12px', borderBottom: '1px solid #e8e8e8', textAlign: 'left' }}>第 {selectedPeriod} 期</th>}
                  {taskInfo.isRecurring && !selectedPeriod && <th style={{ padding: '10px 12px', borderBottom: '1px solid #e8e8e8', textAlign: 'left' }}>期数记录</th>}
                  <th style={{ padding: '10px 12px', borderBottom: '1px solid #e8e8e8', textAlign: 'left' }}>状态</th>
                  {!taskInfo.isPersonal && <th style={{ padding: '10px 12px', borderBottom: '1px solid #e8e8e8', textAlign: 'left' }}>进度</th>}
                  <th style={{ padding: '10px 12px', borderBottom: '1px solid #e8e8e8', textAlign: 'left' }}>附件</th>
                  <th style={{ padding: '10px 12px', borderBottom: '1px solid #e8e8e8', textAlign: 'left' }}>附件上传时间</th>
                  <th style={{ padding: '10px 12px', borderBottom: '1px solid #e8e8e8', textAlign: 'center' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {(taskInfo.members || []).map(member => {
                  const ds = member.displayStatus || member.status;
                  const statusBg = ds === '已完成' ? '#f6ffed' : ds === '未填报' ? '#fff7e6' : '#e6f7ff';
                  const statusColor = ds === '已完成' ? '#52c41a' : ds === '未填报' ? '#fa8c16' : '#1890ff';
                  return (
                  <tr key={member.taskId}>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{member.assigneeName}</td>
                    {taskInfo.isRecurring && selectedPeriod && (
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
                        {(() => {
                          const status = getPeriodStatus(member, Number(selectedPeriod));
                          if (status === '未开始') {
                            return <span style={{ color: '#d9d9d9' }}>—</span>; // 空白占位符
                          }
                          return (
                            <span style={{ color: getStatusColor(status) }}>
                              {status === '已完成' ? '✅ 已完成' : '❌ 未完成'}
                            </span>
                          );
                        })()}
                      </td>
                    )}
                    {taskInfo.isRecurring && !selectedPeriod && (() => {
                      const minP = taskInfo.minPeriod || 1;
                      const maxP = taskInfo.maxPeriod || 1;
                      const totalPeriods = maxP - minP + 1;
                      return (
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0', fontSize: 12 }}>
                          {member.periodRecords && member.periodRecords.length > 0 ? (
                            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${totalPeriods}, 64px)`, gap: 3 }}>
                              {/* 起始期数之前的空白占位 */}
                              {(() => {
                                const startP = member.startPeriod || 1;
                                const placeholders = [];
                                for (let i = minP; i < startP; i++) {
                                  placeholders.push(
                                    <span key={`ph-${i}`} style={{ padding: '2px 4px', borderRadius: 4, background: '#fafafa', color: '#d9d9d9', border: '1px dashed #e8e8e8', textAlign: 'center', fontSize: 10 }}>第{i}期—</span>
                                  );
                                }
                                return placeholders;
                              })()}
                              {member.periodRecords.map(r => (
                                <span key={r.period} style={{ padding: '2px 4px', borderRadius: 4, background: '#f6ffed', color: '#52c41a', textAlign: 'center', fontSize: 10 }}>第{r.period}期✅</span>
                              ))}
                              {/* 未完成的期数（从起始期数开始计算） */}
                              {(() => {
                                const completed = member.periodRecords.map(r => r.period);
                                const startP = member.startPeriod || 1;
                                const missed = [];
                                for (let i = startP; i < maxP; i++) {
                                  if (!completed.includes(i)) missed.push(i);
                                }
                                return missed.map(p => (
                                  <span key={p} style={{ padding: '2px 4px', borderRadius: 4, background: '#fff2f0', color: '#ff4d4f', textAlign: 'center', fontSize: 10 }}>第{p}期❌</span>
                                ));
                              })()}
                            </div>
                          ) : <span style={{ color: '#999' }}>无记录</span>}
                        </td>
                      );
                    })()}
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 4, background: statusBg, color: statusColor }}>{ds}</span>
                    </td>
                    {!taskInfo.isPersonal && <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{member.progress}%</td>}
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
                      {member.attachments && member.attachments.length > 0 ? (
                        <span style={{ color: '#1890ff' }}>{member.attachments.length} 个附件</span>
                      ) : <span style={{ color: '#999' }}>无</span>}
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
                      {member.lastActivityTime ? (
                        <span style={{ color: '#333', fontSize: 13 }}>{member.lastActivityTime}</span>
                      ) : (
                        <span style={{ color: '#ccc', fontSize: 14 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                      <button onClick={() => navigate(`/tasks/${member.taskId}`)} style={{ padding: '4px 12px', background: '#1890ff', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>查看</button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}

      {/* 批量附件下载 - Feature 2: 附件去重显示 */}
      {uniqueAttachments.length > 0 && (
        <div style={{ marginBottom: 24, background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, margin: 0 }}>📦 批量下载附件（共 {uniqueAttachments.length} 个{allAttachments.length > uniqueAttachments.length ? `，去重前 ${allAttachments.length} 个` : ''}，已选 {selectedAttachments.size} 个）</h3>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={toggleSelectAll} style={{ padding: '6px 16px', background: '#f0f0f0', border: '1px solid #d9d9d9', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                {selectedAttachments.size === allAttachments.length ? '取消全选' : '全选'}
              </button>
              <button 
                onClick={downloadSelected} 
                disabled={selectedAttachments.size === 0}
                style={{ 
                  padding: '6px 16px', 
                  background: selectedAttachments.size > 0 ? '#1890ff' : '#f0f0f0', 
                  color: selectedAttachments.size > 0 ? '#fff' : '#999',
                  border: 'none', 
                  borderRadius: 6, 
                  cursor: selectedAttachments.size > 0 ? 'pointer' : 'not-allowed', 
                  fontSize: 13 
                }}>
                📥 下载选中 ({selectedAttachments.size})
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {uniqueAttachments.map(att => (
              <div key={att.id || att.filename} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: selectedAttachments.has(att.id) ? '#e6f7ff' : '#fafafa', borderRadius: 6, border: selectedAttachments.has(att.id) ? '1px solid #91d5ff' : '1px solid #f0f0f0' }}>
                <input 
                  type="checkbox" 
                  checked={selectedAttachments.has(att.id)} 
                  onChange={() => toggleAttachment(att.id)}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <span style={{ flex: 1, color: selectedAttachments.has(att.id) ? '#1890ff' : '#333' }}>
                  📄 {att.originalName || att.filename} <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>{formatSize(att.size)}</span>
                </span>
                <span style={{ color: '#666', fontSize: 12 }}>{att.taskName} - {att.assignee}</span>
                {att.period && <span style={{ padding: '2px 6px', borderRadius: 4, background: '#f0f5ff', color: '#2f54eb', fontSize: 11 }}>第{att.period}期</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
